/**
 * Havva Dose — Quick view trigger.
 * Self-contained: injects its own CSS, so no theme stylesheet is modified.
 * The only theme edit is one <script> tag in layout/theme.liquid, which keeps
 * this portable across Impulse version updates.
 *
 * WHAT IT DOES
 * 1. Replaces Impulse's full-width hover-reveal "Quick view" bar with a small
 *    circular "+" pinned to the bottom-right of the product image, always
 *    visible (the DeFacto pattern).
 * 2. Makes quick view work on touch at all. Impulse blocks it three ways:
 *      a. CSS  .supports-touch .quick-product__btn { display: none }
 *              (theme.js adds `supports-touch` on any touch device — this is
 *               capability-based, NOT a media query, so resizing a desktop
 *               browser will not reproduce it)
 *      b. The button ships with `small--hide` -> display:none !important <768px
 *      c. theme.initQuickShop() only preloads the modal from mouseover/focusin
 *         and early-returns on theme.config.bpSmall. Touch fires neither, so
 *         `btn--not-ready` (opacity:0) is never cleared.
 *
 * Verified against Impulse 9.0.0. If Archetype ever ships native touch quick
 * view, the tap handler here can go; the styling would still apply.
 */
(function () {
  var POLL_MS = 100;
  var TIMEOUT_MS = 9000;

  /* Icon is a background SVG rather than a text glyph so it stays crisp and
     font-independent. The label text is kept in the DOM (font-size:0) so
     screen readers still announce the button. */
  var CSS = [
    '.quick-product__btn,',
    '.supports-touch .quick-product__btn,',
    '.quick-product__btn.small--hide {',
    '  display: flex !important;',
    '  align-items: center;',
    '  justify-content: center;',
    '  width: 32px;',
    '  min-width: 0;',
    '  height: 32px;',
    '  min-height: 0;',
    '  padding: 0;',
    '  left: auto;',
    '  right: 10px;',
    '  bottom: 10px;',
    '  border-radius: 50%;',
    /* Impulse sets overflow:hidden, which would clip the expanded tap
       target drawn by ::before below. */
    '  overflow: visible;',
    '  background-color: #fff;',
    '  background-image: url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 12 12\'%3E%3Cpath d=\'M6 1v10M1 6h10\' stroke=\'%2322242a\' stroke-width=\'1.5\' stroke-linecap=\'round\'/%3E%3C/svg%3E");',
    '  background-repeat: no-repeat;',
    '  background-position: center;',
    '  background-size: 12px 12px;',
    '  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);',
    '  font-size: 0;',
    '  letter-spacing: 0;',
    '  opacity: 1;',
    '  transform: none;',
    /* A running transition outranks !important in the cascade, so keep it
       away from the properties this file controls. */
    '  transition: background-color 0.2s ease, transform 0.2s ease;',
    '}',
    /* Visible circle stays 32px (matching the reference) while the tap target
       is expanded to 48px, which is the accessible minimum. */
    '.quick-product__btn::before {',
    '  content: "";',
    '  position: absolute;',
    '  top: -8px;',
    '  right: -8px;',
    '  bottom: -8px;',
    '  left: -8px;',
    '}',
    '.quick-product__btn:hover {',
    '  background-color: #fff;',
    '  transform: scale(1.08);',
    '}',
    /* RTL: mirror to the bottom-left. */
    "[dir='rtl'] .quick-product__btn,",
    "html[lang^='ar'] .quick-product__btn {",
    '  right: auto;',
    '  left: 10px;',
    '}',
    /* The modal loads on tap here rather than on hover, so the button must
       stay visible while still flagged not-ready. */
    '.quick-product__btn.btn--not-ready { opacity: 1 !important; }',
    ".quick-product__btn[data-qv-loading='true'] {",
    '  opacity: 0.5 !important;',
    '  pointer-events: none;',
    '}',
    /* "View full details" link under add-to-cart. Impulse's quick-shop
       modal ships with no route to the full product page. */
    '.havva-qv-details {',
    '  display: block;',
    '  margin: 14px 0 0;',
    '  text-align: center;',
    '  text-decoration: underline;',
    '  text-underline-offset: 3px;',
    '  font-size: max(calc(var(--typeBaseSize) - 4px), 13px);',
    '  line-height: 1.4;',
    '}'
  ].join('\n');

  /* Bilingual label chosen here rather than via a locale key: ar.json
     carries almost no translations on this store. */
  function addDetailsLink(modal) {
    if (!modal) return false;
    if (modal.querySelector('.havva-qv-details')) return true;

    var form = modal.querySelector('.product-single__form');
    var section = modal.querySelector('[data-product-handle]');
    if (!form || !section) return false;

    var handle = section.getAttribute('data-product-handle');
    if (!handle) return false;

    var root = (window.theme && theme.routes && theme.routes.home) || '/';
    var url = (root + '/products/' + handle).replace('//', '/');

    var isArabic = /^ar/i.test(document.documentElement.lang || '') ||
                   (document.documentElement.dir || '').toLowerCase() === 'rtl';

    var a = document.createElement('a');
    a.className = 'havva-qv-details';
    a.href = url;
    a.textContent = isArabic ? 'عرض التفاصيل الكاملة' : 'View full details';
    form.insertAdjacentElement('afterend', a);
    return true;
  }

  /* The modal's form is not always in the DOM the instant theme.Modals
     binds, so retry briefly rather than assuming it is ready. */
  function ensureDetailsLink(productId) {
    var waited = 0;
    var t = setInterval(function () {
      waited += POLL_MS;
      if (addDetailsLink(document.getElementById('QuickShopModal-' + productId)) ||
          waited >= 3000) {
        clearInterval(t);
      }
    }, POLL_MS);
  }

  function injectCSS() {
    if (document.getElementById('havva-quick-view-touch-css')) return;
    var style = document.createElement('style');
    style.id = 'havva-quick-view-touch-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  onReady(function () {
    if (!window.theme || !theme.settings || !theme.settings.quickView) return;
    if (typeof theme.preloadProductModal !== 'function') return;

    // The circular trigger applies on every device. Only the tap-to-load
    // handler below is really about touch, and it is harmless on desktop,
    // where the theme's hover preload has already cleared btn--not-ready.
    injectCSS();

    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('.quick-product__btn');
      if (!btn) return;

      // Modal already built — let the theme's own handler open it.
      if (!btn.classList.contains('btn--not-ready')) {
        var built = btn.closest('.grid-product');
        if (built && built.dataset.productId) {
          ensureDetailsLink(built.dataset.productId);
        }
        return;
      }

      var grid = btn.closest('.grid-product');
      if (!grid || !grid.dataset.productId) return;

      evt.preventDefault();
      evt.stopPropagation();

      if (btn.dataset.qvLoading === 'true') return;
      btn.dataset.qvLoading = 'true';

      var productId = grid.dataset.productId;
      var handle = grid.dataset.productHandle;

      theme.preloadProductModal(handle, productId, btn);

      // preloadProductModal resolves a fetch with no callback, so poll for the
      // modal it builds. It clears btn--not-ready once Modals binds.
      var waited = 0;
      var timer = setInterval(function () {
        waited += POLL_MS;

        var ready = document.getElementById('QuickShopModal-' + productId) &&
                    !btn.classList.contains('btn--not-ready');

        if (ready) {
          clearInterval(timer);
          btn.dataset.qvLoading = 'false';
          ensureDetailsLink(productId);
          btn.click();
          return;
        }

        if (waited >= TIMEOUT_MS) {
          // Network or template problem — fall back to the product page
          // rather than leaving a dead button.
          clearInterval(timer);
          btn.dataset.qvLoading = 'false';
          var link = grid.querySelector('.grid-product__link');
          if (link && link.href) window.location.href = link.href;
        }
      }, POLL_MS);
    }, true); // capture, so this runs before the theme's own handler
  });
})();
